import { Link } from "react-router-dom";
import { Users, Baby, Timer, Flag, Smartphone, ShieldCheck, ArrowRight, Check } from "lucide-react";
import Seo from "@/lib/Seo";
import { mediaUrl, euros } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";
import { usePrices } from "@/components/home/AppOffers";

const FACTS = [
  { icon: Users, label: "Joueurs", value: "2 à 6" },
  { icon: Baby, label: "Âge", value: "Dès 6 ans" },
  { icon: Timer, label: "Durée", value: "≈ 45 min" },
  { icon: Flag, label: "Fabrication", value: "France" },
];

const GALLERY = [
  { src: null, alt: "Photo réelle de la boîte Douzième Homme — Édition Nice" },
  { src: "/api/uploads/produit-boite-dos.png", alt: "Dos de la boîte du jeu Douzième Homme" },
  { src: "/api/uploads/produit-plateau.png", alt: "Plateau de jeu grand format illustré à la main" },
  { src: "/api/uploads/produit-kit-jeu.png", alt: "Kit de jeu et cartes Équipement supporter" },
  { src: "/api/uploads/produit-pions.png", alt: "Pions en forme de ballon et dé" },
  { src: "/api/uploads/produit-accessoires.png", alt: "Accessoires et cartes du jeu" },
];

const CONTENTS = [
  "1 plateau de jeu grand format illustré à la main",
  "6 pions en forme de ballon",
  "24 cartes Équipement supporter (maillot, écharpe, drapeau, ticket)",
  "500 questions sur cartes numérotées — 4 niveaux de difficulté",
  "Accès à l'app Douzième Homme — +1000 questions bonus en ligne",
  "1 règle du jeu illustrée",
  "1 dé",
];

export default function Jeu() {
  const { content, settings } = useSite();
  const { price1, price2, compareAt2 } = usePrices();
  return (
    <>
      <Seo title="Le Jeu — Édition Nice" path="/jeu"
        description="Découvrez Douzième Homme — Le Jeu : le jeu de plateau des supporters niçois. 2 à 6 joueurs, dès 6 ans, 45 minutes, +500 questions. Conçu à Nice, fabriqué en France." />

      <section className="py-14 sm:py-20 lg:py-28" data-testid="jeu-intro">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-4">Le jeu · Édition Nice</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              Nice se joue à la maison.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {content.hero_subtitle} Un jeu de plateau familial, fait par des supporters pour des supporters,
              qui fait traverser la ville du Vieux-Nice jusqu'à l'Allianz Riviera.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="jeu-facts">
              {FACTS.map(({ icon: Icon, label, value }) => (
                <div key={label} className="bg-white border border-line p-4">
                  <Icon className="w-5 h-5 text-rouge mb-2" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
                  <p className="font-display font-bold text-onyx">{value}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/acheter" data-testid="jeu-cta-acheter"
                className="inline-flex justify-center items-center bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                Acheter — {euros(price1)}
              </Link>
              <Link to="/ou-trouver-le-jeu" data-testid="jeu-cta-points-vente"
                className="inline-flex justify-center items-center border-2 border-onyx text-onyx px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-onyx hover:text-cream transition-colors">
                Trouver un point de vente
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 bg-rouge/5 rotate-2" aria-hidden="true" />
              <img src={mediaUrl("/api/uploads/produit-complet.png")} alt="Contenu complet du jeu Douzième Homme — Édition Nice"
                className="relative w-full" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-onyx text-cream" data-testid="jeu-gallery-section">
        <div className="container-x">
          <Reveal className="mb-10">
            <p className="eyebrow mb-4 !text-rouge-light">Dans la boîte</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1]">
              Un coffret premium, illustré à la main.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jeu-gallery">
            {GALLERY.map((g, i) => (
              <Reveal key={g.alt} delay={i * 0.05}>
                <img src={mediaUrl(g.src || settings?.product_photo_url || "/api/uploads/boite-reelle.jpg")} alt={g.alt} loading="lazy"
                  className={`w-full border border-cream/10 ${g.src ? "bg-white p-4" : "object-cover h-full"}`} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3" data-testid="jeu-contents-list">
              {CONTENTS.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm sm:text-base text-cream/80">
                  <Check className="w-5 h-5 text-rouge-light shrink-0 mt-0.5" /> {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20" data-testid="jeu-app-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="eyebrow mb-4">{content.app_eyebrow}</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase text-onyx">{content.app_title}</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{content.app_text}</p>
            <a href={settings?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="jeu-app-cta"
              className="mt-7 inline-flex items-center gap-2 bg-onyx text-cream px-7 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge transition-colors">
              <Smartphone className="w-4 h-4" /> Accéder à l'application
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={mediaUrl("/api/uploads/app-phone-bg.png")} alt="Application compagnon Douzième Homme sur smartphone"
              className="w-full max-w-sm mx-auto" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-sand border-y border-line" data-testid="jeu-partnership">
        <div className="container-x flex flex-col sm:flex-row items-start gap-6">
          <ShieldCheck className="w-10 h-10 text-rouge shrink-0" />
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase text-onyx">Partenaire OGC Nice</h2>
            <p className="mt-3 text-base text-muted-foreground max-w-2xl">
              Douzième Homme — Le Jeu est partenaire de l'OGC Nice. Le jeu est disponible à la boutique officielle
              OGC Nice et sur le site officiel du club.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="jeu-buy-section">
        <div className="container-x">
          <Reveal className="mb-10">
            <p className="eyebrow mb-4">Passer à l'action</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-onyx">
              Deux formules, zéro excuse.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <Reveal>
              <div className="h-full bg-white border border-line p-8 flex flex-col">
                <h3 className="font-display text-xl font-bold uppercase text-onyx">1 boîte</h3>
                <p className="mt-4 font-display text-4xl font-extrabold text-onyx" data-testid="jeu-price-1">{euros(price1)}</p>
                <Link to="/acheter?offre=1" data-testid="jeu-offer-1-cta"
                  className="mt-6 inline-flex items-center justify-center gap-2 border-2 border-onyx text-onyx px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-onyx hover:text-cream transition-colors">
                  Commander <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full bg-onyx text-cream p-8 flex flex-col">
                <h3 className="font-display text-xl font-bold uppercase">2 boîtes</h3>
                <p className="mt-4 flex items-baseline gap-3">
                  <span className="font-display text-4xl font-extrabold" data-testid="jeu-price-2">{euros(price2)}</span>
                  <span className="text-cream/50 line-through">{euros(compareAt2)}</span>
                </p>
                <p className="text-sm font-bold text-gold mt-1">2 boîtes, un seul envoi</p>
                <Link to="/acheter?offre=2" data-testid="jeu-offer-2-cta"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                  Commander <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
